import axios from "axios"
import Joi from "joi"

import { LINKS } from "@api/links.const"
import { handleError } from "@utils/error.middleware"

import { type APIAccessToken } from "@interfaces/APIAccessToken.interface"
import { type APIError } from "@interfaces/APIError.interface"
import { type APIContactId } from "@interfaces/APIContactId.interface"

const COMMUNICATION_INTEGRITY_ERROR = "Communication integrity error"

export const APIAuth = {
  async checkIfTokenExist(
    token: string,
    refreshToken?: string | null
  ): Promise<APIAccessToken | APIError> {
    try {
      // The stored session travels in the body too: without it the API cannot tell
      // whose session is open, because cookie-blocking browsers send no cookie (PL-243)
      const response = await axios.post(LINKS.AUTH.TOKEN.CHECK_IF_EXIST(), {
        token,
        withRefreshToken: true,
        ...(refreshToken ? { refreshToken } : {})
      })

      const responseSchema = Joi.alternatives().try(
        Joi.object({
          accessToken: Joi.string().required(),
          refreshToken: Joi.string(),
          contactName: Joi.string().required(),
          contactPhone: Joi.string().allow("", null),
          contactEmail: Joi.string().email().allow("", null),
          contactQrId: Joi.string().required(),
          contactQrEnabled: Joi.boolean().required()
        }),
        Joi.object({
          error: Joi.string().required()
        })
      )

      const { error, value } = responseSchema.validate(response.data)
      if (error) return { error: COMMUNICATION_INTEGRITY_ERROR }

      return value
    } catch (error) {
      return handleError(error)
    }
  },
  async checkIfContactExist(phoneOrEmail: string): Promise<APIContactId | APIError> {
    try {
      const response = await axios.post(LINKS.AUTH.CHECK_IF_EXIST_AND_GENERATE_PIN(), {
        phoneOrEmail
      })

      const responseSchema = Joi.alternatives().try(
        Joi.object({
          id: Joi.number().required()
        }),
        Joi.object({
          error: Joi.string().required()
        })
      )

      const { error, value } = responseSchema.validate(response.data)
      if (error) return { error: COMMUNICATION_INTEGRITY_ERROR }

      return value
    } catch (error) {
      return handleError(error)
    }
  },
  async checkIfPinExist(contactId: number, pin: string): Promise<APIError> {
    try {
      const response = await axios.post(LINKS.AUTH.PIN.CHECK_IF_EXIST(), {
        contactId,
        pin,
        withRefreshToken: true
      })

      const responseSchema = Joi.alternatives().try(
        Joi.object({
          accessToken: Joi.string().required(),
          refreshToken: Joi.string(),
          contactName: Joi.string().required(),
          contactPhone: Joi.string().allow("", null),
          contactEmail: Joi.string().email().allow("", null),
          contactQrId: Joi.string().required(),
          contactQrEnabled: Joi.boolean().required()
        }),
        Joi.object({
          error: Joi.string().required()
        })
      )

      const { error, value } = responseSchema.validate(response.data)
      if (error) return { error: COMMUNICATION_INTEGRITY_ERROR }

      return value
    } catch (error) {
      return handleError(error)
    }
  },
  async resendPinCode(contactId: number, isEmail: boolean): Promise<APIError | undefined> {
    try {
      await axios.post(LINKS.AUTH.PIN.RESEND(), { contactId, isEmail })
    } catch (error) {
      return handleError(error)
    }
  },
  async silentRefresh(refreshToken?: string | null) {
    // The refresh token travels in the body too: Safari drops the
    // cross-site refresh cookie, so the cookie alone is not enough
    const { data } = await axios.post(
      LINKS.AUTH.REFRESH(),
      refreshToken ? { refreshToken } : {}
    )
    return data
  },
  // loginRefresh(payload) {
  //   return axios.post(LINKS.LOGIN_REFRESH(), payload)
  // },
  async logout(refreshToken?: string | null): Promise<APIError | undefined> {
    try {
      await axios.post(LINKS.AUTH.LOGOUT(), refreshToken ? { refreshToken } : {})
    } catch (error) {
      return handleError(error)
    }
  }
}
